import { alpha, createTheme } from '@mui/material/styles'
import { green, red } from '@mui/material/colors'
// Registers the MuiPickers* keys on `components` below.
import type {} from '@mui/x-date-pickers/themeAugmentation'

/**
 * Types the custom keys the exported `theme` adds on top of MUI's, so `useTheme()` callers can read
 * them without an `as any` cast (UBQ-5125, A-02). Optional because a theme built elsewhere with
 * `createTheme()` from scratch doesn't carry them.
 */
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        sectionHeading: true
    }
}

declare module '@mui/material/styles' {
    interface Theme {
        tableHeader?: { color: string }
        background?: { color: string }
    }

    /** Tint, edge and heading ink for a popup notice box (UBQ-5808). */
    interface NoticeTone {
        background: string
        border: string
        ink: string
    }

    /**
     * Popup colours that aren't a status colour: the notice boxes (info = teal, warning = amber) and
     * the grey summary box of a confirmation popup (UBQ-5808). Read them through the palette, e.g.
     * `bgcolor: 'notice.warning.background'`, never as hex in a component.
     */
    interface Palette {
        notice: { info: NoticeTone; warning: NoticeTone }
        summary: { background: string; border: string; ink: string }
        /** The pill behind a list or popup search box. */
        searchBar: { background: string }
    }
    /**
     * A heading for a section inside a popup or card body (UBQ-5808): one step below the popup title
     * (16px), so the title always outranks it. Renders as `h3` under the title's `h2`.
     */
    interface TypographyVariants {
        sectionHeading: React.CSSProperties
    }
    interface TypographyVariantsOptions {
        sectionHeading?: React.CSSProperties
    }
    interface PaletteOptions {
        notice?: { info: NoticeTone; warning: NoticeTone }
        summary?: { background: string; border: string; ink: string }
        searchBar?: { background: string }
    }
}

const rawTheme = createTheme({
    palette: {
        primary: {
            light: '#69696a',
            main: '#00797a',
        },
        secondary: {
            light: '#fff5f8',
            main: '#ff3366',

            dark: '#e62958',
        },
        warning: {
            main: '#ffc071',

            // dark: "#ffb25e",
        },
        error: {
            light: red[50],
            main: red[500],
            dark: red[700],
        },
        success: {
            light: green[50],
            main: green[500],
            dark: green[700],
        },
        /* The values the Alerts bulk popups showed before UBQ-5808. Bulk Shipment Completion used
           near-identical shades of its own; both now read these. */
        notice: {
            info: { background: '#e4f1f0', border: '#00797a', ink: '#025b5c' },
            warning: {
                background: '#fdf3e2',
                border: '#b26a00',
                ink: '#8a5a00',
            },
        },
        summary: { background: '#f5f7f7', border: '#e4eaea', ink: '#12201f' },
        searchBar: { background: '#E3F4FF' },
    },
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            '"Helvetica Neue"',
            'Helvetica',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        /* Medium was 700 (i.e. bold) until UBQ-5124. Everything that rendered with it is still bold:
           the headings below and the app's own usages now name `fontWeightBold`, and the MUI
           components that read `fontWeightMedium` internally are pinned to bold in `components`
           below. So 500 changes nothing on screen today; it only makes "medium" mean medium for new
           code. */
        fontWeightMedium: 500,
        fontWeightBold: 700,
    },
    components: {
        /* MUI styles these with `typography.fontWeightMedium`. They rendered bold while medium was
           700, so they are pinned to bold to keep the current look (UBQ-5124). Alert (filled),
           AlertTitle and ListSubheader read it too but aren't used in the app yet; they'll render
           at medium (500) if adopted. */
        /* Info notes use the brand teal instead of MUI's default blue. */
        MuiAlert: {
            styleOverrides: {
                standardInfo: ({ theme }) => ({
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.text.primary,
                    '& .MuiAlert-icon': {
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    fontWeight: theme.typography.fontWeightBold,
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: ({ theme }) => ({
                    fontWeight: theme.typography.fontWeightBold,
                }),
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: ({ theme }) => ({
                    fontWeight: theme.typography.fontWeightBold,
                }),
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-selected': {
                        fontWeight: theme.typography.fontWeightBold,
                    },
                }),
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                labelContainer: ({ theme }) => ({
                    fontWeight: theme.typography.fontWeightBold,
                }),
            },
        },
        /* Popup defaults for every dialog in the app (UBQ-5808), matching the Alerts bulk and Bulk
           Shipment Completion popups. The title keeps the teal and 16px size it inherits from h6;
           only its weight changes. Content padding is MUI's own (16px 24px with dividers, 20px 24px
           without), which is what those popups already showed, so it isn't restated here. A popup should not re-set any of
           these in `sx`; confirmation popups use `Common/ConfirmPopup`. */
        MuiTypography: {
            defaultProps: {
                // Unlisted variants still fall back to MUI's own mapping.
                variantMapping: { sectionHeading: 'h3' },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    // 16px, the h6 size it inherits: 20px read as oversized on smaller screens.
                    fontSize: 16,
                    fontWeight: 600,
                },
            },
        },
        MuiDialogContentText: {
            styleOverrides: {
                root: {
                    // MUI greys this body text (text.secondary); popup bodies use the normal text colour.
                    color: 'inherit',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        opacity: '1', // increase opacity for disabled input
                        color: '#000',
                        WebkitTextFillColor: '#000',
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        opacity: 1,
                        color: '#00000066',
                        WebkitTextFillColor: '#00000066',
                    },
                },
            },
        },
    },
})

/* Headings keep the base font stack each variant already inherits from `rawTheme`. This used to
   name 'Roboto Condensed', which the app never loads, so headings and dialog titles silently fell
   back to the browser's generic sans-serif (Arial on Windows) while everything else rendered in the
   system stack — two typefaces on one screen (UBQ-5807). One app font: never add a `fontFamily`
   in a component. Agreed fallback if the old heading look is ever requested: put
   `fontFamily: "'Roboto Condensed', sans-serif"` back here. That restores it, but headings again
   depend on each PC's fallback font (Arial on Windows, Helvetica on Mac). */
const fontHeader = {
    // color: rawTheme.palette.text.primary,
    color: rawTheme.palette.primary.main,
    fontWeight: rawTheme.typography.fontWeightBold,

    // textTransform: "uppercase",
}

const theme = {
    ...rawTheme,
    palette: {
        ...rawTheme.palette,
        background: {
            ...rawTheme.palette.background,
            default: rawTheme.palette.common.white,
        },
    },
    typography: {
        ...rawTheme.typography,
        fontHeader,
        h1: {
            ...rawTheme.typography.h1,
            ...fontHeader,
            letterSpacing: 0,
            fontSize: 60,
        },
        h2: {
            ...rawTheme.typography.h2,
            ...fontHeader,
            fontSize: 48,
        },
        h3: {
            ...rawTheme.typography.h3,

            // ...fontHeader,
            fontWeight: rawTheme.typography.fontWeightBold,
            fontSize: 24,
        },
        h4: {
            ...rawTheme.typography.h4,
            ...fontHeader,
            fontSize: 20,
        },
        h5: {
            ...rawTheme.typography.h5,
            fontSize: 18,
            fontWeight: rawTheme.typography.fontWeightBold,
        },
        h6: {
            ...rawTheme.typography.h6,
            ...fontHeader,
            fontSize: 16,
        },
        subtitle1: {
            ...rawTheme.typography.subtitle1,
            fontSize: 16,
        },
        body1: {
            ...rawTheme.typography.body1,
            fontWeight: rawTheme.typography.fontWeightRegular,
            fontSize: 14,
        },
        body2: {
            ...rawTheme.typography.body2,
            fontSize: 12,
        },
        sectionHeading: {
            fontFamily: rawTheme.typography.fontFamily,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.5,
            color: rawTheme.palette.primary.main,
        },
        /* These two used to spread the whole `rawTheme.typography` object, which dragged every other
           variant in as nested keys: emotion reads `h1: {...}` or `button: {...}` inside a style as a
           descendant selector, so each MUI Button/Tab carried stray rules for any h1–h6 or button
           inside it. Only these three properties ever applied to the element itself, so naming them
           keeps the rendered text identical (UBQ-5125, T-04). */
        textHeader: {
            fontFamily: rawTheme.typography.fontFamily,
            fontSize: 14,
            fontWeight: rawTheme.typography.fontWeightLight,
        },
        button: {
            fontFamily: rawTheme.typography.fontFamily,
            fontSize: 14,
            fontWeight: rawTheme.typography.fontWeightMedium,
        },
    },
    tableHeader: {
        color: '#5dd0df',
    },
    background: {
        color: '#f1fdff',
    },
}

export default theme
